import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// Stack.jsx — "Arsenal / Loadout": studio tech stack as an inventory + process strip
const Stack = (() => {
  const { Joystick, Bolt, Diamond, Star, Coin, Mushroom, Crown, Book, Heart } = PixelIcons;

  // Real studio stack (per design system notes: Unity, Unreal, React/Vite, Blender, Aseprite, AR/WebGL, Figma, Strapi/Node)
  const tools = [
    { name: "Unity 6",       catK: "stk.cat.engine",  Icon: Joystick, accent: "var(--ragross-magenta)" },
    { name: "Unreal 5",      catK: "stk.cat.engine",  Icon: Crown,    accent: "var(--ragross-cyan)" },
    { name: "React · Vite",  catK: "stk.cat.web",     Icon: Bolt,     accent: "var(--ragross-gold)" },
    { name: "Blender",       catK: "stk.cat.3d",      Icon: Diamond,  accent: "var(--ragross-green)" },
    { name: "Aseprite",      catK: "stk.cat.pixel",   Icon: Star,     accent: "var(--ragross-magenta)" },
    { name: "WebGL · AR",    catK: "stk.cat.xr",      Icon: Mushroom, accent: "var(--ragross-cyan)" },
    { name: "Figma",         catK: "stk.cat.ui",      Icon: Heart,    accent: "var(--ragross-gold)" },
    { name: "Strapi · Node", catK: "stk.cat.backend", Icon: Coin,     accent: "var(--ragross-green)" },
  ];

  const stages = [
    { n: "01", tK: "stk.proc.s1.t", dK: "stk.proc.s1.d", accent: "var(--ragross-magenta)" },
    { n: "02", tK: "stk.proc.s2.t", dK: "stk.proc.s2.d", accent: "var(--ragross-cyan)" },
    { n: "03", tK: "stk.proc.s3.t", dK: "stk.proc.s3.d", accent: "var(--ragross-gold)" },
    { n: "04", tK: "stk.proc.s4.t", dK: "stk.proc.s4.d", accent: "var(--ragross-green)" },
  ];

  const stkStyles = {
    section: {
      padding: "112px 0",
      background:
        "linear-gradient(180deg, rgba(67,0,95,0.04) 0%, rgba(57,34,110,0.10) 100%), var(--bg-app)",
    },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    head: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, marginBottom: 56 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: { font: "var(--type-display-lg)", letterSpacing: "-0.015em", lineHeight: 1.05, color: "#fff", margin: 0 },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", maxWidth: 640, margin: 0 },

    // Inventory grid
    grid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 72 },
    slot: {
      position: "relative",
      background: "var(--ragross-purple-night)",
      border: "2px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      padding: 18,
      display: "flex", flexDirection: "column", gap: 12,
      transition: "transform 200ms var(--ease-arcade), border-color 200ms, box-shadow 200ms",
      overflow: "hidden",
    },
    slotScan: {
      position: "absolute", inset: 0,
      background: "var(--scanlines)", opacity: 0.3, pointerEvents: "none",
    },
    slotCorner: {
      position: "absolute", top: 8, right: 10,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.10em", color: "var(--fg-3)",
    },
    ico: (color) => ({
      width: 52, height: 52,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--ragross-purple-deep)",
      border: `2px solid ${color}`,
      position: "relative", zIndex: 1,
    }),
    name: { font: "var(--type-h3)", color: "#fff", margin: 0, position: "relative", zIndex: 1 },
    cat: (color) => ({
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 8, letterSpacing: "0.12em",
      color, position: "relative", zIndex: 1,
    }),

    // Process strip
    procHead: {
      display: "flex", flexDirection: "column", gap: 10, marginBottom: 32,
    },
    procTag: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 10, letterSpacing: "0.18em",
      color: "var(--ragross-gold)",
    },
    procTitle: { font: "var(--type-display-md)", color: "#fff", margin: 0, lineHeight: 1.1 },
    procGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0,
      border: "1px solid var(--border-soft)", borderRadius: "var(--radius-card)", overflow: "hidden" },
    stage: {
      padding: 24,
      borderRight: "1px solid var(--border-soft)",
      display: "flex", flexDirection: "column", gap: 12,
      background: "var(--bg-surface)",
      position: "relative",
    },
    stageNum: (color) => ({
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 22, color,
      textShadow: `0 0 16px ${color}`,
    }),
    stageT: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: 11, letterSpacing: "0.12em", color: "#fff",
    },
    stageD: { font: "var(--type-body-sm)", color: "var(--fg-2)", margin: 0 },
  };

  function Slot({ tool }) {
    const t = useT();
    const [h, setH] = React.useState(false);
    const Icon = tool.Icon;
    return (
      <div
        style={{
          ...stkStyles.slot,
          borderColor: h ? tool.accent : stkStyles.slot.borderColor,
          transform: h ? "translate(-3px,-3px)" : "none",
          boxShadow: h ? `6px 6px 0 ${tool.accent}` : "none",
        }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
      >
        <div style={stkStyles.slotScan}></div>
        <span style={stkStyles.slotCorner}>EQUIP</span>
        <div style={stkStyles.ico(tool.accent)}><Icon size={26}/></div>
        <h3 style={stkStyles.name}>{tool.name}</h3>
        <span style={stkStyles.cat(tool.accent)}>{t(tool.catK)}</span>
      </div>
    );
  }

  function Stack() {
    const t = useT();
    return (
      <section id="stack" style={stkStyles.section} data-screen-label="05 Arsenal">
        <div style={stkStyles.container}>
          <div style={stkStyles.head}>
            <div style={stkStyles.tag}>
              <span>{t("stk.tag")}</span>
              <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
            </div>
            <h2 style={stkStyles.title} className="section-title">
              {t("stk.title1")} <span style={{ color: "var(--ragross-cyan)" }}>{t("stk.title2")}</span> {t("stk.title3")}
            </h2>
            <p style={stkStyles.lead}>{t("stk.lead")}</p>
          </div>

          <div style={stkStyles.grid} className="responsive-grid-4">
            {tools.map((tool) => <Slot key={tool.name} tool={tool}/>)}
          </div>

          {/* Process strip */}
          <div style={stkStyles.procHead}>
            <span style={stkStyles.procTag}>{t("stk.proc.tag")}</span>
            <h3 style={stkStyles.procTitle} className="section-title">{t("stk.proc.title")}</h3>
          </div>
          <div style={stkStyles.procGrid} className="responsive-grid-4">
            {stages.map((s) => (
              <div key={s.n} style={stkStyles.stage}>
                <span style={stkStyles.stageNum(s.accent)}>{s.n}</span>
                <span style={stkStyles.stageT}>{t(s.tK)}</span>
                <p style={stkStyles.stageD}>{t(s.dK)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return Stack;
})();

export default Stack;
